import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ArrowRightIcon } from 'lucide-react';
import { Button } from '../ui/Button';
export function PricingSection() {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('yearly');
  const [emailCount, setEmailCount] = useState(0);
  const [phoneCount, setPhoneCount] = useState(0);
  const emailCredits = emailCount * 3;
  const phoneCredits = phoneCount * 9;
  const totalCredits = emailCredits + phoneCredits;
  const plans = [{
    name: 'Free',
    credits: 200,
    monthlyPrice: 0,
    yearlyPrice: 0
  }, {
    name: 'Starter',
    credits: 5000,
    monthlyPrice: 150,
    yearlyPrice: 105
  }, {
    name: 'Pro',
    credits: 10000,
    monthlyPrice: 299,
    yearlyPrice: 209
  }, {
    name: 'Scale',
    credits: 40000,
    monthlyPrice: 1070,
    yearlyPrice: 750
  }];
  const getRecommendedPlan = () => {
    for (const plan of plans) {
      if (totalCredits <= plan.credits) {
        const price = billingPeriod === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice;
        const costPerCredit = plan.credits > 0 && plan.monthlyPrice > 0 ? price / plan.credits : 0;
        return {
          name: plan.name,
          credits: plan.credits,
          remaining: plan.credits - totalCredits,
          price,
          costPerCredit
        };
      }
    }
    return {
      name: 'Enterprise',
      credits: 0,
      remaining: 0,
      price: 0,
      costPerCredit: 0
    };
  };
  const recommendedPlan = getRecommendedPlan();
  const totalCost = recommendedPlan.price;
  const emailCostPerLookup = recommendedPlan.costPerCredit * 3;
  const phoneCostPerLookup = recommendedPlan.costPerCredit * 9;
  const tiers = [{
    name: 'Free',
    monthlyPrice: 0,
    yearlyPrice: 0,
    description: 'For early testers and curious explorers',
    cta: 'Try for free',
    highlighted: false,
    features: [{
      section: 'Data & lead generation',
      items: ['200 credits a month', '200M+ local business database', '20 searches', 'Add up to 5K accounts']
    }]
  }, {
    name: 'Starter',
    monthlyPrice: 150,
    yearlyPrice: 105,
    description: 'For users building a scalable outreach workflow',
    cta: 'Get started',
    highlighted: true,
    features: [{
      section: 'Data & lead generation',
      items: ['5k credits/month', 'Export up to 100k businesses with emails a month', '200M+ local business database', 'Add up to 20k accounts', 'Decision maker contacts']
    }]
  }, {
    name: 'Pro',
    monthlyPrice: 299,
    yearlyPrice: 209,
    description: 'For teams needing higher volume and integrations',
    cta: 'Get started',
    highlighted: false,
    features: [{
      section: 'Everything in Starter plan',
      items: ['10k credits/month', 'Export up to 200k businesses with emails', 'Add up to 70k accounts']
    }, {
      section: 'Integration & extensions',
      items: ['Hubspot and Salesforce CRM sync', 'Team invite (for new users)', 'Openmart chrome extension']
    }]
  }, {
    name: 'Scale',
    monthlyPrice: 1070,
    yearlyPrice: 750,
    description: 'For high-volume teams with custom needs',
    cta: 'Get started',
    highlighted: false,
    features: [{
      section: 'Everything in Pro plan',
      items: ['40k credits/month']
    }, {
      section: 'Advanced business discovery',
      items: ['Newly opened businesses', 'Spot trending businesses']
    }, {
      section: 'Real-time slack support',
      items: []
    }]
  }];
  return <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8 mt-24" data-id="element-3160">
      {/* MAIN PRICING CARD */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-50px'
    }} transition={{
      duration: 0.5
    }} className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-12 lg:p-16" data-id="element-3161">
        {/* Header & Toggle */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.5
      }} className="flex flex-col md:flex-row items-center justify-between gap-8 mb-16" data-id="element-3162">
          <div className="text-center md:text-left" data-id="element-3163">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-3164">
              Plans for{' '}
              <span className="text-blue-600" data-id="element-3165">teams of all sizes</span>
            </h2>
            <p className="text-lg text-slate-500" data-id="element-3166">
              Start for free, upgrade when you need more power.
            </p>
          </div>
          <div className="inline-flex items-center bg-slate-50 border border-slate-200 rounded-full p-1.5 shadow-sm" data-id="element-3167">
            <button onClick={() => setBillingPeriod('monthly')} className={`px-6 py-2.5 rounded-full font-bold text-sm transition-all ${billingPeriod === 'monthly' ? 'bg-white text-slate-900 shadow-sm border border-slate-200/50' : 'text-slate-500 hover:text-slate-900'}`} data-id="element-3168">
              Monthly
            </button>
            <button onClick={() => setBillingPeriod('yearly')} className={`flex items-center gap-2 px-6 py-2.5 rounded-full font-bold text-sm transition-all ${billingPeriod === 'yearly' ? 'bg-white text-slate-900 shadow-sm border border-slate-200/50' : 'text-slate-500 hover:text-slate-900'}`} data-id="element-3169">
              Yearly
              <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider" data-id="element-3170">
                Save 30%
              </span>
            </button>
          </div>
        </motion.div>

        {/* Pricing Tiers Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-3171">
          {tiers.map((tier, idx) => {
          const price = billingPeriod === 'yearly' ? tier.yearlyPrice : tier.monthlyPrice;
          const isHighlighted = tier.highlighted;
          return <motion.div key={idx} initial={{
            opacity: 0,
            y: 20,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            y: 0,
            scale: 1
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: idx * 0.08
          }} className={`flex flex-col rounded-3xl p-8 transition-all duration-300 hover:-translate-y-1 ${isHighlighted ? 'bg-slate-900 text-white shadow-xl shadow-slate-900/10' : 'bg-white border border-slate-200 hover:border-slate-300 shadow-sm'}`} data-id="element-3172">
                <div className="mb-8" data-id="element-3173">
                  <h3 className={`text-xl font-bold mb-2 ${isHighlighted ? 'text-white' : 'text-slate-900'}`} data-id="element-3174">
                    {tier.name}
                  </h3>
                  <p className={`text-sm min-h-[40px] ${isHighlighted ? 'text-slate-400' : 'text-slate-500'}`} data-id="element-3175">
                    {tier.description}
                  </p>
                </div>

                <div className="mb-8" data-id="element-3176">
                  <div className="flex items-baseline gap-1" data-id="element-3177">
                    <span className={`text-4xl font-bold ${isHighlighted ? 'text-white' : 'text-slate-900'}`} data-id="element-3178">
                      ${price}
                    </span>
                    {price > 0 && <span className={isHighlighted ? 'text-slate-400' : 'text-slate-500'} data-id="element-3179">
                        /mo
                      </span>}
                  </div>
                  {price > 0 && <div className={`text-xs mt-1 ${isHighlighted ? 'text-slate-500' : 'text-slate-400'}`} data-id="element-3180">
                      Billed{' '}
                      {billingPeriod === 'yearly' ? 'annually' : 'monthly'}
                    </div>}
                </div>

                <div className="mb-8" data-id="element-3181">
                  <Button href="http://app.openmart.com/register" variant={isHighlighted ? 'primary' : 'secondary'} size="md" fullWidth data-id="element-3182">
                    {tier.cta}
                  </Button>
                </div>

                <div className="space-y-6 flex-grow" data-id="element-3183">
                  {tier.features.map((featureGroup, i) => <div key={i} data-id="element-3184">
                      <div className={`text-xs font-bold uppercase tracking-wider mb-4 ${isHighlighted ? 'text-slate-500' : 'text-slate-400'}`} data-id="element-3185">
                        {featureGroup.section}
                      </div>
                      {featureGroup.items.length > 0 && <div className="space-y-3" data-id="element-3186">
                          {featureGroup.items.map((feature, j) => <div key={j} className="flex items-start gap-3" data-id="element-3187">
                              <CheckIcon className={`w-4 h-4 shrink-0 mt-0.5 ${isHighlighted ? 'text-blue-400' : 'text-blue-600'}`} data-id="element-3188" />
                              <span className={`text-sm ${isHighlighted ? 'text-slate-300' : 'text-slate-600'}`} data-id="element-3189">
                                {feature}
                              </span>
                            </div>)}
                        </div>}
                    </div>)}
                </div>
              </motion.div>;
        })}
        </div>

        {/* Enterprise Bar */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="mt-16 pt-8 border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-3190">
          <div className="flex flex-col md:flex-row items-center gap-4 text-center md:text-left" data-id="element-3191">
            <span className="text-2xl font-bold text-slate-900" data-id="element-3192">
              Enterprise
            </span>
            <span className="text-slate-500" data-id="element-3193">
              Need more leads or a custom solution?
            </span>
          </div>
          <Button href="http://app.openmart.com/register" variant="secondary" size="md" data-id="element-3194">
            Contact sales
          </Button>
        </motion.div>
      </motion.div>

      {/* CALCULATOR CARD */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-50px'
    }} transition={{
      duration: 0.5,
      delay: 0.2
    }} className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-12" data-id="element-3195">
        <div className="grid lg:grid-cols-3 gap-12 items-center" data-id="element-3196">
          <div className="lg:col-span-2 space-y-8" data-id="element-3197">
            <div data-id="element-3198">
              <h3 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-3199">
                Usage calculator
              </h3>
              <p className="text-slate-500" data-id="element-3200">
                Estimate your monthly costs based on your outreach volume.
              </p>
            </div>

            <div className="space-y-8" data-id="element-3201">
              {/* Employee Emails */}
              <div data-id="element-3202">
                <div className="flex items-center justify-between mb-4" data-id="element-3203">
                  <label className="text-sm font-bold text-slate-900" data-id="element-3204">
                    Employee emails
                  </label>
                  <div className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full" data-id="element-3205">
                    {emailCount} emails × 3 = {emailCredits} credits
                  </div>
                </div>
                <div className="flex items-center gap-6" data-id="element-3206">
                  <input type="range" min="0" max="100000" step="100" value={emailCount} onChange={e => setEmailCount(Number(e.target.value))} className="flex-1 h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md" style={{
                  background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${emailCount / 100000 * 100}%, #F1F5F9 ${emailCount / 100000 * 100}%, #F1F5F9 100%)`
                }} data-id="element-3207" />
                  <input type="number" value={emailCount} onChange={e => setEmailCount(Math.max(0, Number(e.target.value)))} className="w-24 px-4 py-2 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600" data-id="element-3208" />
                </div>
              </div>

              {/* Employee Phones */}
              <div data-id="element-3209">
                <div className="flex items-center justify-between mb-4" data-id="element-3210">
                  <label className="text-sm font-bold text-slate-900" data-id="element-3211">
                    Employee phones
                  </label>
                  <div className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full" data-id="element-3212">
                    {phoneCount} phones × 9 = {phoneCredits} credits
                  </div>
                </div>
                <div className="flex items-center gap-6" data-id="element-3213">
                  <input type="range" min="0" max="100000" step="100" value={phoneCount} onChange={e => setPhoneCount(Number(e.target.value))} className="flex-1 h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md" style={{
                  background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${phoneCount / 100000 * 100}%, #F1F5F9 ${phoneCount / 100000 * 100}%, #F1F5F9 100%)`
                }} data-id="element-3214" />
                  <input type="number" value={phoneCount} onChange={e => setPhoneCount(Math.max(0, Number(e.target.value)))} className="w-24 px-4 py-2 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-600" data-id="element-3215" />
                </div>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-6 space-y-4" data-id="element-3216">
              <div className="grid grid-cols-2 gap-4" data-id="element-3217">
                <div data-id="element-3218">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-3219">
                    Total credits needed
                  </div>
                  <div className="text-2xl font-bold text-slate-900" data-id="element-3220">
                    {totalCredits.toLocaleString()}
                  </div>
                </div>
                <div data-id="element-3221">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-3222">
                    Estimated cost
                  </div>
                  <div className="text-2xl font-bold text-blue-600" data-id="element-3223">
                    ${totalCost}/mo
                  </div>
                </div>
              </div>
              {totalCredits > 0 && <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500" data-id="element-3224">
                  <span data-id="element-3225">
                    Per email lookup: ~${emailCostPerLookup.toFixed(3)}
                  </span>
                  <span data-id="element-3226">
                    Per phone lookup: ~${phoneCostPerLookup.toFixed(3)}
                  </span>
                </div>}
              <p className="text-xs text-slate-400" data-id="element-3227">
                Basic business info (name, address, website, business email) is
                included free — no credits needed.
              </p>
            </div>
          </div>

          {/* Recommended Plan Box */}
          <div className="lg:col-span-1 bg-slate-50 rounded-3xl p-8 border border-slate-100 h-full flex flex-col justify-center text-center" data-id="element-3228">
            <div className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-3229">
              Recommended Plan
            </div>
            <div className="text-4xl font-bold text-slate-900 mb-4" data-id="element-3230">
              {recommendedPlan.name}
            </div>
            <p className="text-slate-500 text-sm mb-4" data-id="element-3231">
              Based on your usage of{' '}
              <span className="font-bold text-slate-700" data-id="element-3232">
                {totalCredits.toLocaleString()} credits
              </span>
            </p>
            {recommendedPlan.name !== 'Enterprise' ? <div className="text-sm text-slate-500 mb-8" data-id="element-3233">
                <span data-id="element-3234">
                  Includes{' '}
                  <span className="font-bold text-slate-700" data-id="element-3235">
                    {recommendedPlan.credits.toLocaleString()}
                  </span>{' '}
                  credits/month
                </span>
                {recommendedPlan.remaining >= 0 ? <span className="block mt-1 text-emerald-600 font-medium" data-id="element-3236">
                    {recommendedPlan.remaining.toLocaleString()} credits
                    remaining
                  </span> : <span className="block mt-1 text-red-500 font-medium" data-id="element-3237">
                    {Math.abs(recommendedPlan.remaining).toLocaleString()}{' '}
                    credits over — consider upgrading
                  </span>}
              </div> : <p className="text-sm text-slate-500 mb-8" data-id="element-3238">
                Your usage exceeds our standard plans. Contact sales for a
                custom quote.
              </p>}
            <Button href="http://app.openmart.com/register" variant="primary" size="md" arrow fullWidth data-id="element-3239">
              Select plan
            </Button>
          </div>
        </div>
      </motion.div>
    </section>;
}